// When a lazily loaded script fails to load, the part of the page it powers
// stays dead until the reader reloads: client components, or the dialogs and
// menus behind Tailwind Elements. The fetch fails when a deploy has replaced
// the chunk an open tab asks for, or when the reader's network drops the
// request. Vite reports both as a cancelable `vite:preloadError` event on
// window (https://vite.dev/guide/build#load-error-handling), so reload once
// for the reader. The sessionStorage timestamp stops a chunk that keeps
// failing, for example one an extension blocks, from reloading in a loop;
// that failure reaches Sentry as before. A script the page works without
// (src/lib/optional-import.ts) never reloads it: its import just fails.

import { optionalImportLoading } from "@/lib/optional-import";

const RELOAD_KEY = "hh_preload_error_reload_at";
const MIN_RELOAD_INTERVAL_MS = 60_000;

interface ReloadWindow {
  addEventListener: (type: string, listener: (event: Event) => void) => void;
  sessionStorage: Pick<Storage, "getItem" | "setItem">;
  location: Pick<Location, "reload">;
}

/**
 * Installs the handler; the returned function says whether a reload has
 * started. `isOptional` says whether the failure is an optional import's.
 */
export function reloadOnPreloadError(
  win: ReloadWindow,
  now: () => number = Date.now,
  isOptional: () => boolean = optionalImportLoading,
): () => boolean {
  let reloading = false;
  win.addEventListener("vite:preloadError", (event) => {
    // Left alone, Vite rethrows the error to the import, whose caller goes without.
    if (isOptional()) return;
    try {
      const last = Number(win.sessionStorage.getItem(RELOAD_KEY)) || 0;
      if (now() - last < MIN_RELOAD_INTERVAL_MS) return;
      win.sessionStorage.setItem(RELOAD_KEY, String(now()));
    } catch {
      // Blocked storage leaves no loop guard, so let the error through.
      return;
    }
    reloading = true;
    // Stops Vite rethrowing the error from the failed import.
    event.preventDefault();
    win.location.reload();
  });
  return () => reloading;
}
